'use client';

import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  SESSION_ATTACHMENT_MAX_FILE_SIZE_BYTES,
  SESSION_ATTACHMENT_MAX_FILES,
} from '@terra/shared-contracts-conversation-attachments';
import { terraProxyApiClient } from '../../clients/testerClient';

export const CONVERSATION_ATTACHMENTS_QUERY_KEY = 'conversation-attachments';

type UploadConversationAttachmentsArgs = {
  files: File[];
  applicationId?: string;
  onUploadProgress?: (progressEvent: {
    loaded: number;
    total?: number;
  }) => void;
};

export const useConversationAttachments = ({
  threadId,
}: {
  threadId: string;
}) => {
  const queryClient = useQueryClient();

  const query = useQuery({
    queryKey: [CONVERSATION_ATTACHMENTS_QUERY_KEY, threadId],
    queryFn: async () => {
      const body =
        await terraProxyApiClient.listConversationAttachments(threadId);
      return body.attachments;
    },
    enabled: Boolean(threadId),
  });

  const invalidate = async () => {
    await queryClient.invalidateQueries({
      queryKey: [CONVERSATION_ATTACHMENTS_QUERY_KEY, threadId],
    });
  };

  const uploadMutation = useMutation({
    mutationFn: async ({
      files,
      applicationId,
      onUploadProgress,
    }: UploadConversationAttachmentsArgs) => {
      const body = await terraProxyApiClient.uploadConversationAttachments({
        threadId,
        applicationId,
        files,
        onUploadProgress,
      });
      return body.attachments;
    },
    onSuccess: async () => {
      await invalidate();
    },
  });

  const deleteMutation = useMutation({
    mutationFn: async (attachmentId: string) => {
      return terraProxyApiClient.deleteConversationAttachment({
        threadId,
        attachmentId,
      });
    },
    onSuccess: async () => {
      await invalidate();
    },
  });

  return {
    attachments: query.data ?? [],
    refetch: query.refetch,
    invalidate,
    uploadAttachments: uploadMutation.mutateAsync,
    deleteAttachment: deleteMutation.mutateAsync,
    maxFiles: SESSION_ATTACHMENT_MAX_FILES,
    maxFileSizeBytes: SESSION_ATTACHMENT_MAX_FILE_SIZE_BYTES,
  };
};
